/**
 * Human-readable labels for every top-level GraphQL Query field, used by the
 * Scout chat tool-call renderer to render `execute_graphql` invocations as
 * "Looking up <subject>" instead of humanized field names.
 *
 * Why a curated map and not auto-humanization:
 *   - Schema field names are camelCase; auto-humanize produces awkward copy
 *     ("looking up facility chemical measurements for report").
 *   - Acronyms like Tier II / RCRA / EPCRA / CFR / LQG need canonical casing.
 *   - Singular vs plural article handling is fiddly ("a facility" vs
 *     "facility documents") and gets it wrong often enough to embarrass us.
 *   - Some operations have a richer label when you look at the variables —
 *     e.g. `facilities(search: "state:CA")` should read as "facilities in CA"
 *     rather than just "facilities".
 *
 * Each entry's `subject` is dropped into the running "Looking up <subject>"
 * sentence — so it should read as a bare noun phrase ("facilities", "a
 * facility", "Tier II reports", "chemical measurements for a report").
 *
 * `refine` is an optional override that gets the variables and the parsed
 * omnisearch `search` string so it can produce a more specific subject (e.g.
 * "facilities in CA" when the model passed `state:CA`). It returns `null` to
 * fall back to `subject`.
 *
 * Adding a new entry: a CI check (apps/oak/scripts/check-scout-graphql-labels.ts)
 * fails when a Query field exists in the schema but not in this map. Add
 * entries here when the schema grows.
 */

export interface GraphqlLabel {
  /** Bare noun phrase for the "Looking up <subject>" sentence. */
  subject: string;
  /**
   * Optional refinement based on the variables/search string. Return `null`
   * to fall back to `subject`.
   */
  refine?: (variables: Record<string, unknown> | null, search: string | null) => string | null;
}

function summarizeFacilityLookup(search: string | null): string {
  const filters = parseOmnisearchFilters(search);
  const stateFilter = filters.get("state");
  if (stateFilter) {
    const states = stateFilter.split(/[|,]/).filter(Boolean).join(", ");
    return states ? `facilities in ${states}` : "facilities";
  }
  if (filters.has("ids")) return "selected facilities";
  if (filters.has("facilityId")) return "a facility";
  return "facilities";
}

function summarizeDocumentLookup(search: string | null): string {
  const filters = parseOmnisearchFilters(search);
  if (filters.has("facilityId")) return "facility documents";
  return "documents";
}

function summarizeTaskLookup(search: string | null): string {
  const filters = parseOmnisearchFilters(search);
  const status = filters.get("status");
  if (status === "OVERDUE") return "overdue tasks";
  if (status === "ACTIVE") return "active tasks";
  const taskStatus = filters.get("taskStatus");
  if (taskStatus === "OPEN|IN_PROGRESS") return "open tasks";
  if (filters.has("facilityId")) return "facility tasks";
  return "tasks";
}

function parseOmnisearchFilters(search: string | null): Map<string, string> {
  const filters = new Map<string, string>();
  if (!search) return filters;
  const matches = search.matchAll(/([A-Za-z][A-Za-z0-9_]*):("[^"]+"|[^ ]+)/g);
  for (const match of matches) {
    const [, key, rawValue] = match;
    if (!key || !rawValue) continue;
    filters.set(key, rawValue.replace(/"/g, ""));
  }
  return filters;
}

/**
 * Special label used for any GraphQL introspection query (`__type`,
 * `__schema`, `__typename`) — the model probing the schema. Customers don't
 * care about the mechanics; just say what we're actually doing.
 */
export const INTROSPECTION_SUBJECT = "how to pull your Encamp data";

/**
 * Labels keyed by the GraphQL field name selected at the top level of an
 * `execute_graphql` query. This is the schema-defined name, not the
 * model-invented operation name (e.g. `query PickCaFacility { facility(...) }`
 * matches `facility` here, not `PickCaFacility`).
 */
export const GRAPHQL_LABELS: Record<string, GraphqlLabel> = {
  // ------------- Facilities -------------
  facilities: { subject: "facilities", refine: (_v, s) => summarizeFacilityLookup(s) },
  facility: { subject: "a facility" },
  facilitiesByInternalTagName: { subject: "facilities by tag" },
  facilitiesForCredential: { subject: "facilities for a credential" },
  facilitiesWithInsightsCandidates: { subject: "facilities with insights candidates" },
  chemicalEnabledFacilities: {
    subject: "chemical-enabled facilities",
    refine: (_v, s) => summarizeFacilityLookup(s),
  },
  wasteEnabledFacilities: {
    subject: "waste-enabled facilities",
    refine: (_v, s) => summarizeFacilityLookup(s),
  },
  wasteFacilities: { subject: "waste facilities", refine: (_v, s) => summarizeFacilityLookup(s) },
  chemicalFacilities: { subject: "chemical facilities" },
  cersFacilities: { subject: "CERS facilities" },
  deniedFacilities: { subject: "denied facilities" },
  newlyNotReportingFacilities: { subject: "newly-not-reporting facilities" },
  myTierIIFacilities: { subject: "my Tier II facilities" },
  facilityInventoryStatus: { subject: "facility inventory status" },
  facilityMatchesForDocuments: { subject: "facility matches for documents" },
  facilityPilotProgram: { subject: "a facility pilot program" },
  facilityPilotProgramLocals: { subject: "facility pilot program locals" },
  facilityPilotProgramStates: { subject: "facility pilot program states" },
  facilityStateFields: { subject: "facility state fields" },
  facilityUsage: { subject: "facility usage" },
  facilityWasteShipmentCharts: { subject: "facility waste shipment charts" },
  facilityContactsSearch: { subject: "facility contacts" },

  // ------------- Chemicals & Products -------------
  chemicals: { subject: "chemicals" },
  chemicalsByIds: { subject: "chemicals by id" },
  chemicalAnomalies: { subject: "chemical anomalies" },
  chemicalsOverviewMyProgress: { subject: "my chemical reporting progress" },
  chemicalWithAssociatedData: { subject: "a chemical with associated data" },
  chemicalWithAssociatedDataForFacility: {
    subject: "a chemical with associated data for a facility",
  },
  facilityChemical: { subject: "a facility chemical" },
  facilityChemicalByYear: { subject: "a facility chemical by year" },
  facilityChemicals: { subject: "facility chemicals" },
  facilityChemicalMeasurement: { subject: "a facility chemical measurement" },
  facilityChemicalMeasurements: { subject: "facility chemical measurements" },
  facilityChemicalMeasurementsForReport: {
    subject: "chemical measurements for a report",
  },
  facilityChemicalStateField: { subject: "a facility chemical state field" },
  facilityChemicalStateFieldsForFacility: {
    subject: "facility chemical state fields",
  },
  reportingFacilityChemical: { subject: "a reporting chemical" },
  reportingFacilityChemicals: { subject: "reporting chemicals" },
  reportingFacilityChemicalCounts: { subject: "reporting chemical counts" },
  getFacilityChemicalThresholdData: { subject: "facility chemical threshold data" },
  product: { subject: "a product" },
  products: { subject: "products" },
  productForFacility: { subject: "a facility product" },
  productWithFacilityCount: { subject: "a product with facility count" },
  facilityProduct: { subject: "a facility product" },
  facilityProductByYear: { subject: "a facility product by year" },
  facilityProducts: { subject: "facility products" },
  facilityProductMeasurement: { subject: "a facility product measurement" },
  facilityProductMeasurements: { subject: "facility product measurements" },
  productMeasurementsForReport: { subject: "product measurements for a report" },

  // ------------- Tier II Reports -------------
  tierIIReport: { subject: "a Tier II report" },
  tierIIReports: { subject: "Tier II reports" },
  tierIIReportActivitiesInError: { subject: "Tier II report activities in error" },
  tierIIReportAllDocuments: { subject: "all documents for a Tier II report" },
  tierIIReportContacts: { subject: "Tier II report contacts" },
  tierIIReportDocumentIssues: { subject: "Tier II report document issues" },
  tierIIReportReviewStepValidator: { subject: "Tier II report review-step validation" },
  tierIIReportSnapshotContent: { subject: "a Tier II report snapshot" },
  tierIIReportSubmittedDocuments: { subject: "submitted Tier II report documents" },
  tierIIReportVerifyStepValidator: { subject: "Tier II report verify-step validation" },
  facilityTierIIReports: { subject: "Tier II reports for a facility" },
  facilityReportsList: { subject: "the facility reports list" },
  problematicReports: { subject: "problematic reports" },
  reportSafeToReset: { subject: "whether a report is safe to reset" },
  bulkReportsSafeToReset: { subject: "which reports are safe to reset in bulk" },
  bulkTierIIReportVerifierValidator: { subject: "bulk Tier II report verifier validation" },
  myReportStatus: { subject: "my report status" },
  reportingSeasonSummary: { subject: "the reporting season summary" },
  getAllTierIIReportTags: { subject: "all Tier II report tags" },

  // ------------- Documents -------------
  document: { subject: "a document" },
  documents: { subject: "documents", refine: (_v, s) => summarizeDocumentLookup(s) },
  documentManifestDetail: { subject: "document manifest detail" },
  documentAIEnrichmentJob: { subject: "a document AI enrichment job" },
  getAcceptableFileFormatsByDocumentType: { subject: "acceptable file formats by document type" },
  getDocumentDownloadLink: { subject: "a document download link" },
  getEmbeddedDocumentPreparationUrl: { subject: "an embedded document preparation URL" },
  getEmbeddedDocumentSigningUrl: { subject: "an embedded document signing URL" },
  getBoldSignDocumentInfo: { subject: "BoldSign document info" },
  checkForFile: { subject: "whether a file exists" },
  readT2sFile: { subject: "a T2S file" },

  // ------------- Submissions & Reviews -------------
  submission: { subject: "a submission" },
  submissionComments: { subject: "submission comments" },
  submissionErrorStats: { subject: "submission error stats" },
  submissionInputValidator: { subject: "submission input validation" },
  submissionInternalComments: { subject: "internal submission comments" },
  submissionVirtualEvents: { subject: "submission tasks" },
  bulkSubmissionsSafeToSendToReviewers: {
    subject: "which submissions are safe to send to reviewers",
  },
  bulkSubmissionsSafeToUpdate: { subject: "which submissions are safe to update" },
  outstandingReviews: { subject: "outstanding reviews" },
  revisionCandidates: { subject: "revision candidates" },
  getRevisionSubmissions: { subject: "revision submissions" },
  encampFilingStatus: { subject: "Encamp filing status" },

  // ------------- Tasks / Calendar -------------
  virtualEvent: { subject: "a task" },
  virtualEvents: { subject: "tasks", refine: (_v, s) => summarizeTaskLookup(s) },
  suggestedTasks: { subject: "suggested tasks" },
  taskCalendarFacilitySubscriptionUrl: { subject: "the facility task calendar URL" },
  taskCalendarSubscription: { subject: "the task calendar subscription" },

  // ------------- Waste -------------
  wasteSubmissionDetail: { subject: "waste submission detail" },
  wasteSubmissionsForStaff: { subject: "waste submissions for staff" },
  wasteSubmissionsForStaffPicker: { subject: "waste submissions for the staff picker" },
  wasteSubmissionsStaffSummary: { subject: "the waste submissions staff summary" },
  wasteSubmissionsSummary: { subject: "the waste submissions summary" },
  wasteFacilitiesByGeneratorStatusAndState: {
    subject: "waste facilities by generator status and state",
  },
  wasteManagementMethod: { subject: "a waste management method" },
  wasteManifestLineItems: { subject: "waste manifest line items" },
  wasteManifestVendors: { subject: "waste manifest vendors" },
  wasteManifestVendorsTable: { subject: "the waste manifest vendors table" },
  wasteLineUnifiedInputValidator: { subject: "waste line input validation" },
  wasteLinesUnified: { subject: "waste lines" },
  wasteStream: { subject: "a waste stream" },
  wasteStreamDestinations: { subject: "waste stream destinations" },
  wasteStreamInputValidator: { subject: "waste stream input validation" },
  wasteStreamInputValidatorForTenant: {
    subject: "organization-scoped waste stream input validation",
  },
  wasteStreamShipmentsByMonth: { subject: "waste stream shipments by month" },
  wasteTotalWaste: { subject: "total waste" },
  wasteVendorTotals: { subject: "waste vendor totals" },
  wastePipelineRunMetadata: { subject: "waste pipeline run metadata" },
  wastePipelineRunRejectedRows: { subject: "waste pipeline rejected rows" },
  wastePipelineRunRows: { subject: "waste pipeline rows" },
  mergedWasteStreamDestinations: { subject: "merged waste stream destinations" },
  originalWasteStreamsById: { subject: "original waste streams by id" },
  bulkManifestDiscrepancies: { subject: "bulk manifest discrepancies" },
  manifestDiscrepancies: { subject: "manifest discrepancies" },
  manifestEditInputValidator: { subject: "manifest edit input validation" },
  processedManifests: { subject: "processed manifests" },
  generatorStatusSummary: { subject: "the generator status summary" },
  staffGeneratorStatusChanges: { subject: "staff generator status changes" },
  rcraInfoCredentialEpaIdMappings: { subject: "RCRAInfo credential EPA ID mappings" },
  rcraInfoHdHandler: { subject: "an RCRAInfo handler" },
  rcraInfoHdHandlers: { subject: "RCRAInfo handlers" },

  // ------------- Organizations & Users & People -------------
  tenant: { subject: "an organization" },
  tenantInputValidator: { subject: "organization input validation" },
  tenants: { subject: "organizations" },
  copyableDemoTenants: { subject: "copyable demo organizations" },
  demoAccountCandidates: { subject: "demo account candidates" },
  user: { subject: "a user" },
  users: { subject: "users" },
  userInputValidator: { subject: "user input validation" },
  staffUsers: { subject: "staff users" },
  getCurrentUser: { subject: "the current user" },
  getUserById: { subject: "a user by id" },
  person: { subject: "a person" },
  people: { subject: "people" },
  peopleByIds: { subject: "people by ids" },
  personFacilities: { subject: "a person's facilities" },
  personFacilityContactsInputValidator: { subject: "person facility contact input validation" },
  profile: { subject: "the user profile" },
  roles: { subject: "roles" },
  staffTenantSubscriptions: { subject: "staff organization subscriptions" },
  canCurrentUserOverrideResetSafetyCheck: {
    subject: "whether the current user can override reset safety",
  },

  // ------------- Credentials -------------
  credential: { subject: "a credential" },
  credentials: { subject: "credentials" },
  credentialInputValidator: { subject: "credential input validation" },
  credentialImplementationForStaff: { subject: "a credential implementation for staff" },
  credentialImplementationsForStaff: { subject: "credential implementations for staff" },
  steersCredentialsPageData: { subject: "the credentials page data" },

  // ------------- Communications & Notifications -------------
  communication: { subject: "a communication" },
  communications: { subject: "communications" },
  communicationValidator: { subject: "communication input validation" },
  notifications: { subject: "notifications" },

  // ------------- Agencies & Lookups -------------
  fireDepartment: { subject: "a fire department" },
  fireDepartments: { subject: "fire departments" },
  fireDepartmentInputValidator: { subject: "fire department input validation" },
  fireDepartmentSearch: { subject: "fire department search" },
  fireDepartmentsPicker: { subject: "the fire department picker" },
  groupedFireDepartmentSearch: { subject: "grouped fire department search" },
  policeDepartment: { subject: "a police department" },
  policeDepartments: { subject: "police departments" },
  policeDepartmentInputValidator: { subject: "police department input validation" },
  lepc: { subject: "an LEPC" },
  lepcs: { subject: "LEPCs" },
  lepcInputValidator: { subject: "LEPC input validation" },
  lepcSearch: { subject: "LEPC search" },
  groupedLepcSearch: { subject: "grouped LEPC search" },
  sercAgencies: { subject: "SERC agencies" },
  sercAgency: { subject: "a SERC agency" },
  sercAgencyByState: { subject: "the SERC agency for a state" },
  leadAgencies: { subject: "lead agencies" },
  leadAgency: { subject: "a lead agency" },
  leadAgencyInputValidator: { subject: "lead agency input validation" },
  t2mAgencies: { subject: "T2M agencies" },
  amazonWadmItems: { subject: "Amazon WADM items" },
  counties: { subject: "counties" },
  municipalities: { subject: "municipalities" },
  discoverLocalJurisdictions: { subject: "local jurisdictions" },
  alternateIdInfo: { subject: "alternate id info" },
  lookupRecordByUuid: { subject: "a record by UUID" },

  // ------------- Insights -------------
  insightsCandidates: { subject: "insights candidates" },
  insightsCohorts: { subject: "insights cohorts" },
  insightsEchoAllFacilities: { subject: "ECHO insights for all facilities" },
  insightsEchoMyFacilities: { subject: "ECHO insights for my facilities" },
  insightsFacilityMix: { subject: "the facility-mix insight" },
  insightsNonCompliance: { subject: "non-compliance insights" },
  insightsPenalties: { subject: "penalty insights" },
  insightsViolations: { subject: "violation insights" },
  echoExporter: { subject: "an ECHO exporter" },
  echoExporters: { subject: "ECHO exporters" },
  echoOverview: { subject: "the ECHO overview" },
  npdesDetail: { subject: "NPDES detail" },
  npdesFacilities: { subject: "NPDES facilities" },
  encampStats: { subject: "Encamp stats" },

  // ------------- Cohorts -------------
  cohort: { subject: "a cohort" },
  cohorts: { subject: "cohorts" },
  cohortMembers: { subject: "cohort members" },
  errorsBlockingCohort: { subject: "errors blocking a cohort" },
  reopenCohortPreview: { subject: "the reopen-cohort preview" },
  strandedCohortMembers: { subject: "stranded cohort members" },
  waitForAllCohortReport: { subject: "the wait-for-all-cohort report" },

  // ------------- Workflow / Jobs / Errors -------------
  job: { subject: "a job" },
  jobs: { subject: "jobs" },
  jobLogs: { subject: "job logs" },
  jobStepsDone: { subject: "completed job steps" },
  webAutomationJob: { subject: "a web automation job" },
  webAutomationJobs: { subject: "web automation jobs" },
  workflowActivities: { subject: "workflow activities" },
  workflowManagerConfigRules: { subject: "workflow manager config rules" },
  workflowManagerConfigs: { subject: "workflow manager configs" },
  workflowOverviewSeasonSummary: { subject: "the workflow overview season summary" },
  isWorkflowConfigManager: { subject: "whether the user is a workflow config manager" },
  errorClusterForWorkflow: { subject: "the error cluster for a workflow" },
  errorClusterGroupsByRunId: { subject: "error cluster groups by run id" },
  errorClusterRuns: { subject: "error cluster runs" },
  errorClusteringConfig: { subject: "error clustering config" },
  errorClustersByRunId: { subject: "error clusters by run id" },
  prolificIssues: { subject: "prolific issues" },
  activities: { subject: "activities" },
  activityLogs: { subject: "activity logs" },
  loadMoreAuditEntries: { subject: "more audit entries" },
  pipeline: { subject: "a pipeline" },
  pipelines: { subject: "pipelines" },
  totalQuantityByTsdf: { subject: "total quantity by TSDF" },
  getRequiredStaffInputs: { subject: "required staff inputs" },
  getLinearIssues: { subject: "Linear issues" },

  // ------------- Validators (input checks) -------------
  chemicalInputValidator: { subject: "chemical input validation" },
  documentInputValidator: { subject: "document input validation" },
  facilityChemicalInputValidator: { subject: "facility chemical input validation" },
  facilityChemicalMeasurementInputValidator: {
    subject: "facility chemical measurement input validation",
  },
  facilityChemicalStorageLocationInputValidator: {
    subject: "facility chemical storage location input validation",
  },
  facilityContactInputValidator: { subject: "facility contact input validation" },
  facilityInputValidator: { subject: "facility input validation" },
  facilityProductInputValidator: { subject: "facility product input validation" },
  facilityProductMeasurementInputValidator: {
    subject: "facility product measurement input validation",
  },
  facilityProductStorageLocationInputValidator: {
    subject: "facility product storage location input validation",
  },
  productChemicalInputValidator: { subject: "product chemical input validation" },
  productInputValidator: { subject: "product input validation" },
  regulatoryInputValidator: { subject: "regulatory input validation" },
  reportingFacilityChemicalInputValidator: {
    subject: "reporting facility chemical input validation",
  },
  undergroundStorageTankInputValidator: {
    subject: "underground storage tank input validation",
  },
  undergroundStorageTankFields: { subject: "underground storage tank fields" },

  // ------------- Tags & Storage -------------
  tags: { subject: "tags" },
  internalTagsByName: { subject: "internal tags by name" },
  getAllWorkflowTags: { subject: "all workflow tags" },
  storageLocations: { subject: "storage locations" },

  // ------------- Payments & Misc -------------
  payment: { subject: "a payment" },
  payments: { subject: "payments" },
  portal: { subject: "the portal" },
  portalSearch: { subject: "the portal search" },
  requirements: { subject: "requirements" },

  // ------------- Scout (its own data) -------------
  getConversationHistory: { subject: "the conversation history" },
  getScoutConversations: { subject: "Scout conversations" },
  getScoutConversationContextSummaryForStaff: {
    subject: "Scout conversation context summary",
  },
  getScoutConversationContextRoundtripsForStaff: {
    subject: "Scout conversation roundtrips",
  },
  getScoutConversationContextPartsForStaff: {
    subject: "Scout conversation message parts",
  },
  getScoutConversationContextDocumentsForStaff: {
    subject: "Scout conversation document attachments",
  },
  getScoutConversationContextPreludeForStaff: {
    subject: "Scout conversation system prompt",
  },
};
